import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';

import { FunctionComponent } from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { useGetFeatureFlagAsync } from '@/features/featuresFlag/apis';
import { FEATURES_FLAG_KEYS } from '@/features/featuresFlag/apis/featureFlag.keys';
import { MainLayout } from '@/features/layouts';
import AuthenticatedTemplateManager from '@/lib/react-msal/AuthenticatedTemplateManager';

import './App.css';
import { Loading, ErrorServiceFallback } from './components';
import { SubscribePublicForm } from './features/borderCrossingRORO/onBoarding';
import { AppProvider } from './providers';

const App: FunctionComponent<{ instance: PublicClientApplication }> = ({ instance }) => {
  const { pathname } = window.location;

  const { data: isBpeEnabled, isLoading: isBpeLoading } = useGetFeatureFlagAsync({
    featureFlagKey: FEATURES_FLAG_KEYS.BPE,
  });

  const { isLoading: isBpeWithRoroLoading } = useGetFeatureFlagAsync({
    featureFlagKey: FEATURES_FLAG_KEYS.BPE_WITH_RORO,
  });

  const { data: isSherpassEnabled, isLoading: isSherpassLoading } = useGetFeatureFlagAsync({
    featureFlagKey: FEATURES_FLAG_KEYS.SHERPASS,
  });

  const { isLoading: isBpeUkEuWithRoroLoading } = useGetFeatureFlagAsync({
    featureFlagKey: FEATURES_FLAG_KEYS.BPE_UKEU_WITH_RORO,
  });

  const { isLoading: isEloAutomatedGenerationForFullLoading } = useGetFeatureFlagAsync({
    featureFlagKey: FEATURES_FLAG_KEYS.ELO_AUTOMATED_GENERATION,
  });

  const { isLoading: isEloAutomatedGenerationForEmptyLoading } = useGetFeatureFlagAsync({
    featureFlagKey: FEATURES_FLAG_KEYS.ELO_AUTOMATED_GENERATION,
  });

  const { isLoading: isGmrAutomatedGenerationLoading } = useGetFeatureFlagAsync({
    featureFlagKey: FEATURES_FLAG_KEYS.GMR_AUTOMATED_GENERATION,
  });

  if (
    isBpeLoading ||
    isSherpassLoading ||
    isBpeWithRoroLoading ||
    isBpeUkEuWithRoroLoading ||
    isGmrAutomatedGenerationLoading ||
    isEloAutomatedGenerationForFullLoading ||
    isEloAutomatedGenerationForEmptyLoading
  ) {
    return <Loading sx={{ height: 1000 }} />;
  }

  if (isBpeEnabled) {
    return <AppProvider />;
  }

  if (isSherpassEnabled) {
    return pathname === '/subscribe' ? (
      <MsalProvider instance={instance}>
        <Router>
          <Routes>
            <Route
              path='/subscribe'
              element={
                <MainLayout>
                  <SubscribePublicForm />
                </MainLayout>
              }
            />
          </Routes>
        </Router>
      </MsalProvider>
    ) : (
      <MsalProvider instance={instance}>
        <AuthenticatedTemplateManager>
          <AppProvider />
        </AuthenticatedTemplateManager>
      </MsalProvider>
    );
  }

  return <ErrorServiceFallback />;
};

export default App;
